import { reactive, Ref, toRefs, watch } from "vue";

interface CssProperties {
  [key: string]: string;
}

interface State {
  properties: CssProperties;
}

interface CssPropertyManager {
  properties: Ref<CssProperties>;
  setProperty: (name: string, value: string) => void;
  setProperties: (props: CssProperties) => void;
  getProperty: (name: string) => string | null;
}

export function useCssProperty(initProps: CssProperties): CssPropertyManager {
  const state = reactive<State>({
    properties: { ...initProps },
  });

  const setProperty = (name: string, value: string): void => {
    state.properties[name] = value;
    const elem = document.querySelector(":root") as any;
    elem?.style.setProperty(`--${name}`, value);
  };

  const setProperties = (props: CssProperties): void => {
    Object.keys(props).forEach((name) => setProperty(name, props[name]));
  };

  const getProperty = (name: string): string | null => {
    const root = document.querySelector(":root");
    if (!root) return null;

    const value = getComputedStyle(root).getPropertyValue(`--${name}`).trim();
    return value || null;
  };

  watch(
    () => state.properties,
    (newProps: CssProperties, oldProps: CssProperties) => {
      const changedProps = Object.keys(newProps).filter((key) => newProps[key] !== oldProps[key]);
      const propsToUpdate = Object.fromEntries(changedProps.map((key) => [key, newProps[key]]));
      setProperties(propsToUpdate);
    },
    { deep: true },
  );

  setProperties(initProps);

  return {
    ...toRefs(state),
    setProperty,
    setProperties,
    getProperty,
  };
}
